import { Supertype, supertypeClass, property, Remoteable, Persistable } from '@haventech/amorphic';
import { ExistingPolicies } from './ExistingPolicies';
import { Customer } from './Customer';
import { Person } from './Person';
import { ExistingPolicy as GQLExistingPolicy } from '@hit/hit-api-client';

//While not all of these are used in typescript code, equivalent strings are in the application metadata.
//We include the full set here for completeness and future-proofing
export enum ExistingPolicyType {
  TERM = 'term',
  UNIVERSAL = 'universal',
  VARIABLE = 'variable',
  WHOLE = 'whole',
  DEFERRED_FIXED_ANNUITY = 'deferred_fixed_annuity',
  VARIABLE_ANNUITY = 'variable_annuity',
  INCOME_ANNUITY = 'income_annuity',
}

@supertypeClass({ toClient: true, toServer: true })
export class ExistingPolicy extends Persistable(Remoteable(Supertype)) implements GQLExistingPolicy {
  @property({ getType: () => ExistingPolicies })
  existingPolicies: ExistingPolicies;

  @property({ rule: ['required'] })
  companyName: string;

  @property({ rule: ['required'] })
  otherCompanyName: string;

  @property({ validate: '' })
  number: string;

  @property({ rule: ['required', 'currency', 'numeric'], validate: 'isWithin(0, 10000000)' })
  face: number;

  @property({
    values: ['term', 'whole', 'deferred', 'income', 'notsure'],
    descriptions: {
      term: 'Term',
      whole: 'Universal Life/Whole Life',
      deferred: 'Deferred Annuity',
      income: 'Income Annuity',
      notsure: 'Not Sure',
    },
  })
  type: string;

  @property({ rule: 'numeric', validate: '' })
  issueYear: number;

  @property({ values: ['Business', 'Personal'], descriptions: { Business: 'Business', Personal: 'Personal' } })
  purpose: string;

  @property({
    rule: ['required'],
    values: ['Applying', 'Inforce'],
    descriptions: { Applying: 'Applying', Inforce: 'Inforce' },
  })
  status: string;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'confirms if the person who applies for the policy is the owner of the existing policy',
  })
  isPolicyOwner: boolean;

  @property({
    rule: ['required', 'replacements'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'determines if the policy being applied going to replace the existing policy in force',
  })
  isReplacement: boolean;

  @property({
    rule: ['required', 'replacements'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'check if funds from the contracts in this existing policy be used to pay premium for policy being applied',
  })
  isReplacementFunds: boolean;

  @property({ getType: () => Customer })
  customer: Customer;

  @property({
    values: function () {
      return this.getOwnerInsuredValues();
    },
    descriptions: function () {
      return this.getOwnerInsuredDescriptions();
    },
    comment: 'Owner of existing policy',
  })
  owner: string;

  @property({
    values: function () {
      return this.getOwnerInsuredValues();
    },
    descriptions: function () {
      return this.getOwnerInsuredDescriptions();
    },
    comment: 'Insured of existing policy',
  })
  insured: string;

  @property({
    comment: 'Name of the existing policy owner',
  })
  ownerName: string;

  @property({
    comment: 'Name of the insured for the existing policy',
  })
  insuredName: string;

  @property({
    rule: ['required', 'currency', 'numeric'],
    comment: 'When the existing policy is surrendered, what will its cash value be when transferred?',
  })
  cashValueTransfer: number;

  @property({
    rule: ['required', 'currency', 'numeric'],
    comment: 'Approximate loan amount to be carried over to new policy as part of Section 1035(a) exchange',
  })
  replacementLoanAmount: number = 0;

  @property()
  isMec: boolean;

  @property()
  is1035Exchange: boolean;

  @property()
  costBasis: number;

  @property({
    comment: 'Reason for replacing the existing policy or contract',
  })
  replacementPurpose: string;

  @property({
    values: ['thisPolicy', 'existingPolicy', 'asCash'],
    descriptions: {
      thisPolicy: 'To this MassMutual policy when issued',
      existingPolicy: 'To the existing policy specified above',
      asCash: 'As cash (Proceeds are issued to the original Owner)',
    },
    comment: 'Method of refunding any balance of unused premiums on the existing policy',
  })
  reimburseMethod: string;

  @property({
    rule: ['required', 'date', 'pastDate'],
    comment: 'Coverage start date',
  })
  coverageStartDate: Date;

  @property({
    comment: 'Premium amount',
  })
  premium: number;

  @property({
    values: ['year', 'quarter', 'month'],
    descriptions: {
      year: 'Year',
      quarter: 'Quarter',
      month: 'Month',
    },
    comment: 'Payment frequency',
  })
  paymentFrequency: string;

  @property({
    values: ['acceptAll', 'acceptBestOffer', 'unsure'],
    descriptions: {
      acceptAll: 'Accept all pending coverage',
      acceptBestOffer: 'Accept just the best offer',
      unsure: 'Not Sure',
    },
    comment: 'Does the owner intend to accept all pending coverage or just the best offer?',
  })
  pendingCoverageIntent: string;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Is existing policy a group policy being replaced, Default value undefined means not answered',
  })
  isGroupPolicy: boolean;

  constructor() {
    super();
  }

  static new(customer) {
    return new ExistingPolicy().init(customer);
  }

  init(customer) {
    this.customer = customer;
    return this;
  }

  getInsuredOrAnnuitant() {
    if (this.existingPolicies) {
      return (<Person>this.existingPolicies.customer.application.primaryPolicy.insured.party)
        .getFullName()
        .substr(0, 21);
    }
  }

  getCompanyNameOrOtherCompanyName() {
    if (this.companyName === 'other') {
      return this.otherCompanyName.substr(0, 21);
    } else {
      return this.companyName.substr(0, 21);
    }
  }

  getRorF(): string {
    if (this.isReplacement) {
      return 'R';
    }
    if (this.isReplacementFunds) {
      return 'F';
    }
    return '';
  }

  getOwnerInsuredValues(): object {
    const values = ['owner'];
    // add a third option for insured if owner and insured are different
    if (!this.customer.application.primaryPolicy.isOwnerSameAsInsured()) {
      values.push('insured');
    }
    values.push('other');
    return values;
  }

  get displayType(): string {
    return {
      term: 'Term Life Insurance',
      deferred_fixed_annuity: 'Deferred Fixed Annuity',
      variable_annuity: 'Variable Annuity',
      income_annuity: 'Income Annuity',
      universal: 'Universal Life Insurance',
      whole: 'Whole Life Insurance',
      variable: 'Variable Life Insurance',
    }[this.type];
  }

  getOwnerInsuredDescriptions(): object {
    const desc = {
      owner: 'Owner',
      insured: 'Insured',
      other: 'Other',
    };
    if (this.customer.application.primaryPolicy.insuredType === 'you') {
      desc['insured'] = 'You';
    } else {
      desc['insured'] = this.customer.application.primaryPolicy.insured.person.firstName;
    }

    if (this.customer.application.primaryPolicy.ownerType === 'you') {
      desc['owner'] = 'You';
    } else {
      if (this.customer.application.primaryPolicy.owner.emailName) {
        desc['owner'] = this.customer.application.primaryPolicy.owner.emailName;
      } else {
        desc['owner'] = 'Owner';
      }
    }
    return desc;
  }

  typeTrigger() {
    if (!this.policyTypeSupports1035Exchanges() && this.cashValueTransfer > 0) {
      this.cashValueTransfer = 0;
      this.customer.application.primaryPolicy.removeExchange1035();
    }
  }

  policyTypeSupports1035Exchanges(): boolean {
    return (
      this.type === ExistingPolicyType.UNIVERSAL ||
      this.type === ExistingPolicyType.VARIABLE ||
      this.type === ExistingPolicyType.WHOLE
    );
  }

  getOwnerOrInsuredName() {
    return this.ownerName || this.insuredName;
  }

  isNWMReplacement(): boolean {
    return this.companyName === 'Northwestern Mutual' && this.isReplacement;
  }

  cashValueTransferTrigger() {
    const policy = this.customer.application.primaryPolicy;
    if (policy.isWholeLifePolicy()) {
      if (this.cashValueTransfer && this.cashValueTransfer > 0) {
        policy.hasAlirRider = true;
        policy.hasAlirScheduledPayments = false;
        policy.alirLumpSumUses1035 = true;
        policy.alirRider.exchange1035.cashValueTransfer = this.cashValueTransfer;
        policy.alirRider.exchange1035.setCostBasisToCashValueTransfer();
      } else if (!this.customer.application.primaryPolicy.insured.existingPolicies.hasCashValueReplacement()) {
        this.customer.application.primaryPolicy.removeExchange1035();
      }
    }
  }
}
